import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from "recharts";

export function StatCard({ label, value, tint = "indigo", testid }) {
  const tints = {
    indigo: "bg-indigo-50 text-indigo-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
  };
  return (
    <div
      data-testid={testid}
      className="rounded-2xl border border-black/10 bg-white p-4"
    >
      <p className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${tints[tint]}`}>{label}</p>
      <p className="mt-2 text-3xl font-black leading-none text-black">{value ?? 0}</p>
    </div>
  );
}

function shortDate(d) {
  // "2026-02-14" -> "Feb 14"
  try {
    const dt = new Date(d);
    return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return d; }
}

export function ActivityLineChart({ series = [], keys, testid = "chart-activity" }) {
  const data = series.map((r) => ({ ...r, label: shortDate(r.date) }));
  return (
    <div data-testid={testid} className="rounded-2xl border border-black/10 bg-white p-4">
      <p className="mb-3 text-sm font-bold text-black/70">Daily activity</p>
      <div style={{ width: "100%", height: 240 }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#666" }} />
            <YAxis tick={{ fontSize: 11, fill: "#666" }} allowDecimals={false} />
            <Tooltip
              contentStyle={{ borderRadius: 10, border: "1px solid rgba(0,0,0,0.08)", fontSize: 12 }}
              labelStyle={{ color: "#111", fontWeight: 600 }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {keys.map((k) => (
              <Line
                key={k.key}
                type="monotone"
                dataKey={k.key}
                name={k.label}
                stroke={k.color}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function TopBarChart({ data = [], nameKey = "name", valueKey = "count", title = "Top clicks", color = "#6366f1", testid = "chart-top" }) {
  if (!data.length) {
    return (
      <div data-testid={testid} className="rounded-2xl border border-black/10 bg-white p-4">
        <p className="mb-3 text-sm font-bold text-black/70">{title}</p>
        <p className="text-sm text-black/40">Nothing to show yet — clicks will appear here as visitors interact.</p>
      </div>
    );
  }
  const shortened = data.map((d) => ({ ...d, __short: (d[nameKey] || "").length > 24 ? (d[nameKey] || "").slice(0, 22) + "…" : d[nameKey] }));
  return (
    <div data-testid={testid} className="rounded-2xl border border-black/10 bg-white p-4">
      <p className="mb-3 text-sm font-bold text-black/70">{title}</p>
      <div style={{ width: "100%", height: Math.min(60 + shortened.length * 34, 380) }}>
        <ResponsiveContainer>
          <BarChart data={shortened} layout="vertical" margin={{ top: 5, right: 16, left: 4, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" horizontal={false} />
            <XAxis type="number" tick={{ fontSize: 11, fill: "#666" }} allowDecimals={false} />
            <YAxis
              type="category"
              dataKey="__short"
              width={160}
              tick={{ fontSize: 11, fill: "#333" }}
            />
            <Tooltip
              contentStyle={{ borderRadius: 10, border: "1px solid rgba(0,0,0,0.08)", fontSize: 12 }}
              labelFormatter={(_, payload) => payload?.[0]?.payload?.[nameKey] || ""}
            />
            <Bar dataKey={valueKey} fill={color} radius={[0, 6, 6, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function RangeToggle({ value, onChange, testid = "range-toggle", theme = "light" }) {
  // `theme="dark"` renders correctly on dark backgrounds (Linkit dashboard).
  // Default `theme="light"` keeps the existing Aly admin panel look.
  const options = [
    { v: 7, label: "7d" },
    { v: 30, label: "30d" },
    { v: 90, label: "90d" },
  ];
  const dark = theme === "dark";
  return (
    <div data-testid={testid} className={`inline-flex rounded-full p-1 text-xs font-semibold ${dark ? "bg-white/10" : "bg-black/10"}`}>
      {options.map((o) => {
        const active = value === o.v;
        const cls = active
          ? "bg-white text-black shadow"
          : dark ? "text-white/70 hover:text-white" : "text-black/70 hover:text-black";
        return (
          <button
            key={o.v}
            data-testid={`range-${o.v}`}
            onClick={() => onChange(o.v)}
            className={`rounded-full px-3 py-1 transition ${cls}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
